import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { practiceService } from '../models/database';
import { sortPracticesByDistance } from '../utils/locationUtils';

const ContactInfo = ({ icon, title, content }) => {
  return (
    <div className="flex items-start mb-6">
      <div className="text-accent mr-4 mt-1">
        {icon}
      </div>
      <div>
        <h3 className="font-bold text-lg mb-1">{title}</h3>
        <p className="text-gray-700">{content}</p>
      </div>
    </div>
  );
};

const Contact = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    postcode: '',
    issue: ''
  });
  const [practices, setPractices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [captchaToken, setCaptchaToken] = useState(null);
  
  // Fetch practices on component mount
  useEffect(() => {
    fetchPractices();
  }, []);
  
  // Fetch all practices
  const fetchPractices = async () => {
    try {
      setLoading(true);
      const practicesData = await practiceService.getAllPractices();
      setPractices(practicesData);
      setError(null);
    } catch (err) {
      console.error('Error fetching practices:', err);
      setError('Failed to load practices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  // Function to handle form submission
  const handleCaptchaSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.postcode) {
      setError('Please enter your postcode to find the nearest practice.');
      return;
    }

    // Execute reCAPTCHA when form is submitted
    window.grecaptcha.ready(() => {
      window.grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY, {action: 'submit'})
        .then(token => {
          // After getting token, proceed with form submission
          submitForm(token);
        })
        .catch(err => {
          console.error('reCAPTCHA error:', err);
          setError('Failed to verify you are not a robot. Please try again.');
        });
    });
  };

  // Function to submit the form after reCAPTCHA verification
  const submitForm = async (captchaToken) => {
    if (!captchaToken) {
      setError('Please verify that you are not a robot.');
      return;
    }
    
    try {
      setLoading(true);
      
      // Sort practices by distance from the user's postcode
      const sortedPractices = await sortPracticesByDistance(formData.postcode, practices);
      
      // Ensure sortedPractices is an array
      const practicesArray = Array.isArray(sortedPractices) ? sortedPractices : [];
      
      if (practicesArray.length === 0) {
        setError('No practices found. Please try a different postcode.');
        setLoading(false);
        return;
      }
      
      // Navigate to the AvailabilityChecker component with the form data and nearest practice
      navigate('/availability-check', { 
        state: { 
          formData,
          nearestPractice: practicesArray[0],
          sortedPractices: practicesArray.slice(0, 5) // Send the 5 nearest practices
        } 
      });
      
    } catch (err) {
      console.error('Error finding nearest practice:', err);
      setError('Failed to find nearest practice. Please try again.');
      setLoading(false);
    }
  };
  return (
    <div id="contact-section" className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row items-center">
          <div className="md:w-1/2 mb-8 md:mb-0 md:pr-8">
            <img 
              src="https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80" 
              alt="Dental patient" 
              className="rounded-lg shadow-lg w-full"
            />
          </div>
          
          <div className="md:w-1/2">
            <h2 className="text-3xl md:text-4xl font-bold font-heading mb-4">
              Contact Us Today
            </h2>
            <p className="text-gray-700 mb-8">
              Our emergency dental team is ready to help you with same-day appointments. Get in touch with us using any of the methods below.
            </p>
            
            <div className="space-y-2">
              {/* <ContactInfo 
                icon={
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                }
                title="Phone"
                content={<a href="tel:07457411865" className="text-accent hover:underline">07457 411 865</a>}
              />
              
              <ContactInfo 
                icon={
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                }
                title="Email"
                content={<a href="mailto:info@dentalhero.co.uk" className="text-accent hover:underline">info@dentalhero.co.uk</a>}
              /> */}
              
              <ContactInfo 
                icon={
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                }
                title="Location"
                content="United Kingdom (excluding Northern Ireland)"
              />
              
              <ContactInfo 
                icon={
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                }
                title="Hours"
                content="Emergency services available 7 days a week, 8am - 8pm"
              />
            </div>

            {/* Instant Availability Checker */}
            <div className="mt-8 bg-white p-6 rounded-lg shadow-lg">
              <h2 className="text-2xl font-bold text-primary font-heading mb-4">
                Check available appointments
              </h2>
              <p className="text-gray-700 mb-4">
              See what appointments we have available at a time that suits you.  
              </p>
              
              <form onSubmit={handleCaptchaSubmit} className="space-y-4">
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your Name"
                  className="w-full p-3 border border-gray-300 rounded"
                  required
                />
                
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Phone Number"
                  className="w-full p-3 border border-gray-300 rounded"
                  required
                />
                
                <input
                  type="text"
                  name="postcode"
                  value={formData.postcode}
                  onChange={handleChange}
                  placeholder="Your Postcode"
                  className="w-full p-3 border border-gray-300 rounded"
                  required
                />
                
                {error && (
                  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-sm">
                    {error}
                  </div>
                )}
                
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full bg-accent hover:bg-accent/90 text-white font-medium py-3 px-6 rounded transition-colors ${
                    loading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Finding nearest practice...
                    </span>
                  ) : (
                    'Find an appointment'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
